import { useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import {
  Alert,
  Box,
  Text,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import {
  EmployeeSelectorV2ItemsDocument,
  ProjectManagementProjectRoleRoleEnum,
} from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { CreateProjectProps } from 'modules/project_management/components/wizards/AddProjectWizard/types'

import { CreateProjectRolesSelector } from './CreateProjectRolesSelector'

export const RolesSelectionStep = ({
  data,
  onDataChanged,
}: WizardStepRenderParams<CreateProjectProps>) => {
  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })

  const [fetchEmployees] = useLazyQuery(EmployeeSelectorV2ItemsDocument)

  useOnNext(() => {
    track(trackingEvents.projectManagement.wizard.projectRolesProjectWizard, {
      owners: data.projectRoles.filter((pr) => pr.role === 'owner').length,
      editors: data.projectRoles.filter((pr) => pr.role === 'editor').length,
      viewers: data.projectRoles.filter((pr) => pr.role === 'viewer').length,
    })
  })

  const onUpdateRow = async (employeeId: number, role: ProjectManagementProjectRoleRoleEnum) => {
    const index = data.projectRoles.findIndex((pr) => pr.employee.id === employeeId)
    if (index !== -1) {
      const projectRole = data.projectRoles[index]
      if (projectRole) {
        projectRole.role = role
      }
      onDataChanged({ ...data })
    } else {
      // Fetch employee details for new employee
      const result = await fetchEmployees({ variables: { ids: [employeeId] } })
      const employeeName = result.data?.employees?.employeeNamesConnection?.nodes?.[0]

      if (employeeName) {
        data.projectRoles.push({
          role,
          employee: {
            id: employeeName.id,
            fullName: employeeName.fullName,
            firstName: employeeName.firstName,
            lastName: employeeName.lastName,
            preferredName: employeeName.preferredName,
            access: {
              avatar: employeeName.access?.avatar ?? null,
            },
          },
        })
        onDataChanged({ ...data })
      }
    }
  }

  const onDeleteRow = (employeeId: number) => {
    data.projectRoles = data.projectRoles.filter((pr) => pr.employee.id !== employeeId)
    onDataChanged({ ...data })
  }

  const resetEmployeeSelection = () => {
    setEmployeeSelection({ type: 'manual', ids: [] })
  }

  const disabledEmployeeIds = data.projectRoles.map((pr) => pr.employee.id)

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Text.Header2 weight="semibold">
          {i18n.t('project_management.modals.create_project.wizard.step_two.header')}
        </Text.Header2>
        <CreateProjectRolesSelector
          onUpdateRow={onUpdateRow}
          onDeleteRow={onDeleteRow}
          projectRoles={data.projectRoles}
          excludeEmployeeIds={disabledEmployeeIds}
          resetEmployeeSelection={resetEmployeeSelection}
          employeeSelection={employeeSelection}
          onSelectEmployees={setEmployeeSelection}
        />
      </Box>
      <Box
        flexDirection="column"
        justifyContent="flexStart"
        maxWidth="s200"
        gap="s8"
        paddingTop="s64"
      >
        <Alert color="info">
          {i18n.t('project_management.modals.create_project.wizard.step_two.alert')}
        </Alert>
      </Box>
    </Box>
  )
}
